import { useCallback, useEffect, useState } from 'react'
import {
  HARBOUR_BOARD_SECTION,
  HARBOUR_DEFAULT_HAS_PHONE,
  HARBOUR_DEFAULT_HIDE_PARKED,
  HARBOUR_DEFAULT_OWNER_FILTER,
  HARBOUR_DEFAULT_PLAN_FILTER,
  HARBOUR_DEFAULT_THRIVING,
} from 'src/constants/harbour'
import { fetchHarbourNeedsYou, fetchHarbourWork } from 'src/utils/harbourApi'
import { createEmptyHarbourBoard } from 'src/utils/harbourMappers'

function useHarbourBoardSection({
  ownerFilter = HARBOUR_DEFAULT_OWNER_FILTER,
  planFilter = HARBOUR_DEFAULT_PLAN_FILTER,
  hideParked = HARBOUR_DEFAULT_HIDE_PARKED,
  hasPhone = HARBOUR_DEFAULT_HAS_PHONE,
  thriving = HARBOUR_DEFAULT_THRIVING,
  enabled = true,
  fetcher,
  emptySection,
  fallbackMessage,
}) {
  const [board, setBoard] = useState(() =>
    createEmptyHarbourBoard(emptySection),
  )
  const [loading, setLoading] = useState(Boolean(enabled))
  const [error, setError] = useState(null)
  const [reloadToken, setReloadToken] = useState(0)

  const refresh = useCallback(() => {
    setReloadToken(token => token + 1)
  }, [])

  useEffect(() => {
    if (!enabled) {
      return undefined
    }

    let cancelled = false
    setLoading(true)
    setError(null)

    async function loadBoard() {
      try {
        const filters = { ownerFilter, planFilter, hideParked, hasPhone }
        if (thriving) {
          filters.thriving = true
        }
        const next = await fetcher(filters)
        if (cancelled) {
          return
        }
        setBoard(next)
      } catch (err) {
        if (cancelled) {
          return
        }
        setBoard(createEmptyHarbourBoard(emptySection))
        setError(err.message || fallbackMessage)
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadBoard()

    return () => {
      cancelled = true
    }
  }, [
    ownerFilter,
    planFilter,
    hideParked,
    hasPhone,
    thriving,
    enabled,
    reloadToken,
    fetcher,
    emptySection,
    fallbackMessage,
  ])

  return { board, loading, error, refresh }
}

/**
 * Today + My work. Independent of Needs you so the chef list paints first.
 */
export function useHarbourWork({
  ownerFilter = HARBOUR_DEFAULT_OWNER_FILTER,
  planFilter = HARBOUR_DEFAULT_PLAN_FILTER,
  hideParked = HARBOUR_DEFAULT_HIDE_PARKED,
  hasPhone = HARBOUR_DEFAULT_HAS_PHONE,
  enabled = true,
} = {}) {
  return useHarbourBoardSection({
    ownerFilter,
    planFilter,
    hideParked,
    hasPhone,
    thriving: false,
    enabled,
    fetcher: fetchHarbourWork,
    emptySection: HARBOUR_BOARD_SECTION.WORK,
    fallbackMessage: 'Failed to load Harbour work',
  })
}

/**
 * Capped Needs you. May skeleton while Today is already on screen.
 * Thriving is session-only — never a sticky refill of the at-risk pile.
 */
export function useHarbourNeedsYou({
  ownerFilter = HARBOUR_DEFAULT_OWNER_FILTER,
  planFilter = HARBOUR_DEFAULT_PLAN_FILTER,
  hideParked = HARBOUR_DEFAULT_HIDE_PARKED,
  hasPhone = HARBOUR_DEFAULT_HAS_PHONE,
  thriving = HARBOUR_DEFAULT_THRIVING,
  enabled = true,
} = {}) {
  return useHarbourBoardSection({
    ownerFilter,
    planFilter,
    hideParked,
    hasPhone,
    thriving,
    enabled,
    fetcher: fetchHarbourNeedsYou,
    emptySection: HARBOUR_BOARD_SECTION.NEEDS_YOU,
    fallbackMessage: 'Failed to load Harbour Needs you',
  })
}
